import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { catchError, finalize, Observable, shareReplay, switchMap, throwError } from 'rxjs';
import { Auth } from '../../features/auth/services/auth';
import { AuthPage } from '../../features/auth/pages/auth-page/auth-page';
import { AuthStep } from '../../features/auth/interfaces/auth.interface';
import { Snackbar } from '../services/snackbar';

/**
 * Endpoints where a 401 means "wrong credentials", not "session expired" — a
 * failed sign-in must show its own error, not bounce the user into the very
 * dialog they are already using.
 *
 * refresh-token is on the list for a different reason. Auth.refreshSession()
 * goes out through HttpClient, so it passes through this interceptor like any
 * other request; without the exemption, a failed refresh would trigger a
 * refresh, and so on.
 */
const AUTH_ENDPOINTS = [
  '/login/login',
  '/login/send-login-otp',
  '/login/verify-login-otp',
  '/login/refresh-token',
];

/**
 * A page fires several requests at once and every one of them 401s on an
 * expired token. Without sharing, each would start its own refresh — and since
 * refreshing rotates the token, all but the first would present one the server
 * had just revoked, which it reads as a stolen token and kills the session.
 *
 * One refresh; every waiter gets its result.
 */
let refreshInFlight: Observable<string> | null = null;

/**
 * A page can fire several requests at once, and every one of them will 401 on an
 * expired token. Without this they would each open a dialog.
 */
let sessionPromptOpen = false;

const isAuthEndpoint = (url: string): boolean => {
  const path = url.toLowerCase();
  return AUTH_ENDPOINTS.some((endpoint) => path.includes(endpoint.toLowerCase()));
};

const refreshOnce = (auth: Auth): Observable<string> => {
  if (!refreshInFlight) {
    refreshInFlight = auth.refreshSession().pipe(
      // finalize sits before shareReplay so it runs once, when the HTTP call
      // settles — not once per waiting request.
      finalize(() => (refreshInFlight = null)),
      shareReplay(1),
    );
  }
  return refreshInFlight;
};

export const tokenInterceptor: HttpInterceptorFn = (req, next: HttpHandlerFn) => {
  const authService = inject(Auth);
  const dialog = inject(MatDialog);
  const snackbar = inject(Snackbar);

  const withToken = (request: HttpRequest<unknown>): HttpRequest<unknown> => {
    const token = authService.token();
    return token ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : request;
  };

  /** The session is over. Sign them out and ask them back in. */
  const endSession = (): void => {
    authService.clearSession();

    if (sessionPromptOpen) {
      return;
    }
    sessionPromptOpen = true;
    snackbar.info('Your session has expired. Please sign in again.');

    dialog
      .open(AuthPage, {
        width: '750px',
        maxHeight: '80vh',
        disableClose: true,
        autoFocus: false,
        data: { step: AuthStep.LOGIN },
      })
      .afterClosed()
      .subscribe(() => {
        sessionPromptOpen = false;
      });
  };

  return next(withToken(req)).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401 || isAuthEndpoint(req.url)) {
        return throwError(() => error);
      }

      // Nothing to spend. This is the old behaviour, and the only path that
      // used to exist.
      if (!authService.getRefreshToken()) {
        endSession();
        return throwError(() => error);
      }

      // The access token expired mid-use. Renew it and run the request again,
      // so the customer notices nothing — least of all mid-checkout.
      return refreshOnce(authService).pipe(
        catchError(() => {
          // Refresh token missing, expired or revoked. Now it is genuinely over.
          // The basket is deliberately left alone so they can sign back in and
          // carry on where they were.
          endSession();
          return throwError(() => error);
        }),
        switchMap(() =>
          next(withToken(req)).pipe(
            catchError((retryError: HttpErrorResponse) => {
              // A brand-new token that is still rejected is not something
              // another refresh would fix.
              if (retryError.status === 401) {
                endSession();
              }
              return throwError(() => retryError);
            }),
          ),
        ),
      );
    }),
  );
};
